// src/modules/project-task/ProjectTaskActions.tsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faTrash,
  faPlus,
  faFileUpload,
  faComments,
  faChevronDown,
  faChevronUp,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { UploadContexts, EntityType } from "../../../constants/globalConstants";
import { TaskDTO, NestedTaskDTO } from "./TaskApi";
import { TaskAssignmentModal } from "../../task-assignment/TaskAssignmentModal";

// Permission utilities
import {
  PermissionAction,
  applyPermissionsToActions,
  readUserFromLocalStorage,
} from "../../../utils/permissions";

// Use a single source of truth for module names:
// Ensure these exist/exported in your app's global constants.
import { MODULES } from "../../../constants/globalConstants";

interface TaskActionsProps {
  task: NestedTaskDTO;
  levelNumber: string;
  maxLevelReached: boolean;
  handleDelete: (id: string) => void;
  handleCreateTask: (task: TaskDTO, levelNumber: string) => void;
}

type ActionItem = {
  icon: any;
  label: string;
  className: string;
  onClick: () => void;
  disabled?: boolean;
  moduleName?: string;            // per-action module override
  required: PermissionAction | PermissionAction[] | string; 
};

const ProjectTaskActions: React.FC<TaskActionsProps> = ({
  task,
  levelNumber,
  maxLevelReached,
  handleDelete,
  handleCreateTask,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const user = readUserFromLocalStorage(); // read once; util also defaults internally if omitted

  const handleAssignmentClick = () => setShowAssignModal(true);

  // Build full list, then permission-filter/disable.
  // NOTE: Each button can specify its own moduleName; if omitted, default is MODULES.PROJECT_TASKS.
  let actionButtons: ActionItem[] = [
    {
      icon: faEye,
      label: "View",
      className: "bg-blue-600 hover:bg-blue-700",
      onClick: () => navigate(`/project-tasks/${task.id}`),
      moduleName: MODULES.PROJECT_TASKS, // viewing project tasks
      required: "view",
    },
    {
      icon: faEdit,
      label: "Edit",
      className: "bg-yellow-500 hover:bg-yellow-600",
      onClick: () => navigate(`/project-tasks/${task.id}/edit`),
      moduleName: MODULES.PROJECT_TASKS,
      required: "edit",
    },
    {
      icon: faPlus,
      label: "Add Sub-Task",
      className: maxLevelReached
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-green-600 hover:bg-green-700",
      disabled: maxLevelReached,
      onClick: () => !maxLevelReached && handleCreateTask(task, levelNumber),
      moduleName: MODULES.PROJECT_TASKS, // creating a project sub-task
      required: "create",
    },
    {
      icon: faTrash,
      label: "Delete",
      className: "bg-red-600 hover:bg-red-700",
      onClick: () => handleDelete(task.id),
      moduleName: MODULES.PROJECT_TASKS,
      required: "delete",
    },
    {
      icon: faFileUpload,
      label: "Add Docs",
      className: "bg-purple-600 hover:bg-purple-700",
      onClick: () =>
        navigate("/uploads", {
          state: {
            context: UploadContexts.TASK_COMMENTS_FILES,
            entityId: task.id,
            entityType: EntityType.PROJECT_TASK,
            metadata: {
              taskName: task.title,
              taskLevel: levelNumber,
              status: task.status,
              projectId: (task as any).projectId ?? task.projectId ?? null,
              projectName: (task as any).projectName ?? null,
              priority: (task as any).priority ?? null,
            },
            title: "Upload Supporting Documents for Project Task",
            description: "Upload documents related to this project task.",
            uploadType: "TASK_SUPPORT_DOCS",
            maxFiles: 10,
            allowedFileTypes: [
              "application/pdf",
              "image/*",
              "application/msword",
              "video/*",
            ],
          },
        }),
      // Docs typically require ability to modify a record; map to "edit"
      moduleName: MODULES.UPLOADS ?? MODULES.PROJECT_TASKS, // fall back to project tasks if you don't have a dedicated "Uploads" module
      required:  ["create", "edit","view"],
    },
    {
      icon: faComments,
      label: "Add Comments",
      className: "bg-blue-500 hover:bg-blue-600",
      onClick: () =>
        navigate("/comments", {
          state: {
            context: UploadContexts.TASK_COMMENTS_FILES,
            entityId: task.id,
            entityType: EntityType.PROJECT_TASK,
            metadata: {
              taskName: task.title,
              taskLevel: levelNumber,
              status: task.status,
              projectId: (task as any).projectId ?? task.projectId ?? null,
              projectName: (task as any).projectName ?? null,
              priority: (task as any).priority ?? null,
            },
            title: "Comments for Project Task",
            description: "Comments related to this project task.",
            uploadType: "TASK_SUPPORT_DOCS",
            maxFiles: 10,
            allowedFileTypes: [
              "application/pdf",
              "image/*",
              "application/msword",
              "video/*",
            ],
          },
        }),
      moduleName: MODULES.COMMENTS ?? MODULES.PROJECT_TASKS, // fall back if not present
      required:  ["create", "edit","view"],
    },
    {
      icon: faUsers,
      label: "Assign",
      className: "bg-indigo-600 hover:bg-indigo-700",
      onClick: handleAssignmentClick,
      // If you track assignment permissions under a specific module, set it here.
      // Otherwise use PROJECT_TASKS edit permission.
      moduleName: MODULES.PROJECT_TASKS,
      required:  ["create", "edit"],
    },
  ];

  // Apply permissions:
  // - defaultModuleName is PROJECT_TASKS
  // - mode "hide" removes unauthorized actions; change to "disable" to keep them visible but disabled.
  actionButtons = applyPermissionsToActions(actionButtons, MODULES.PROJECT_TASKS, "hide", user);

  return (
    <td className="p-2 border space-y-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300 flex items-center justify-center gap-2 text-xs font-medium"
        title={expanded ? "Hide Actions" : "Show Actions"}
      >
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
        {expanded ? "Hide Actions" : "Show Actions"}
      </button>

      {expanded ? (
        <div className="space-y-1 transition-all duration-300 ease-in-out">
          {actionButtons.map(({ icon, label, className, onClick, disabled }) => (
            <button
              key={label}
              className={`${className} text-white px-2 py-1 rounded w-full flex items-center justify-center gap-2 disabled:opacity-50`}
              onClick={onClick}
              disabled={!!disabled}
              title={label}
            >
              <FontAwesomeIcon icon={icon} />
              {label}
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1 mt-1 transition-all duration-300 ease-in-out">
          {actionButtons.map(({ icon, label, className, onClick, disabled }) => (
            <button
              key={label}
              className={`${className} text-white p-2 rounded flex items-center justify-center disabled:opacity-50`}
              onClick={onClick}
              disabled={!!disabled}
              title={label}
            >
              <FontAwesomeIcon icon={icon} />
            </button>
          ))}
        </div>
      )}

      {showAssignModal && (
        <TaskAssignmentModal
          isOpen={showAssignModal}
          task={task}
          entityType={EntityType.PROJECT_TASK}
          taskType="PROJECT TASK"
          entityId={task.id}
          onClose={() => setShowAssignModal(false)}
        />
      )}
    </td>
  );
};

export default ProjectTaskActions;